import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchSetting,
  updateSetting
} from "src/actions/community/settingsActions";
import { fetchBundle } from "../../actions/community/bundlesActions";
import { fetchApp, fetchApps } from "../../actions/community/appsActions";

export interface ProductInterface {
  product_id: string;
  value: number;
}

export type ProductsType = Array<ProductInterface>;
export type RemoveAdsType = {
  native: number;
  banner: number;
  app_open: number;
  interstitial: number;
  interstitial_long: number;
};
export type AllowRemoveAdsType = {
  native: boolean;
  banner: boolean;
  app_open: boolean;
  interstitial: boolean;
  interstitial_long: boolean;
};
export type DailyRewardsType = {
  day1: number;
  day2: number;
  day3: number;
  day4: number;
  day5: number;
  day6: number;
  day7: number;
  day8: number;
  day9: number;
  day10: number;
  day11: number;
  day12: number;
};
export type PostCreateType = {
  purchase_type: 'free' | 'coins' | 'ads';
  coins: number;
}
export type PostPublishType = {
  free_actions: number;
  purchase_type: 'free' | 'coins' | 'ads';
  coins: number;
}
export type PostPurchaseType = {
  free_actions: number;
  purchase_type: 'free' | 'coins' | 'ads';
  coins: number;
  bundle_rules: boolean;
}

export interface SettingInterface {
  id: number;
  target_type: 'App' | 'Bundle';
  target_id: number;
  data: {
    purchase: Object;
    products: ProductsType;
    remove_ads: RemoveAdsType;
    allow_remove_ads: AllowRemoveAdsType;
    daily_rewards: DailyRewardsType;
    post_publish: PostPublishType;
    community_purchase: PostPurchaseType;
    content_purchase: PostPurchaseType;
    community_create: PostCreateType;
    content_create: PostCreateType;
  };
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';

  editId: EntityId | null;
  editError: string | null;

  newParams: SettingInterface | undefined;
  newError: string | null;

  reorderIds: number[] | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,

  reorderIds: null,
};

const adaptor = createEntityAdapter<SettingInterface>();

const slice = createSlice({
  name: 'settings',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #show
      .addCase(fetchSetting.fulfilled, (state, action) => {
        const setting = Object.values(action.payload.setting)[0];
        state.entities[setting.id] = setting;
      })

      // #update
      .addCase(updateSetting.fulfilled, (state, action) => {
        const setting = Object.values(action.payload.setting)[0];
        state.entities[setting.id] = setting;
      })
      .addCase(updateSetting.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #bundle
      .addCase(fetchBundle.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.setting };
      })

      // #app
      .addCase(fetchApp.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.setting };
      })
      .addCase(fetchApps.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.settings };
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setEditError
} = slice.actions;

export const {
  selectIds: selectSettingIds,
  selectEntities: selectSettingEntities,
  selectById: selectSettingById
} = adaptor.getSelectors<RootState>(state => state.community.settings);
